/*
 * 回忆点滴三层共用的两个纯函数。
 *
 * 放在 feature 里而不是 `components/`：`components/README.md` 的收录标准是
 * 「至少被两个 feature 用到」，这两个只有 memory 一家在用。
 */

/**
 * 日记正文按中文句末标点断行。
 *
 * 旧版 `CharacterRelationDiaryModule.tsx:90-97` 就是这么处理的，读起来像一页
 * 信纸而不是一段接口返回的字符串。首行缩进交给 CSS 的 `text-indent`——
 * 往文本里塞全角空格会被一起复制走，也会进到无障碍朗读里。
 *
 * 结尾标点后面会分出一个空串，过滤掉，否则最后多一个空行。
 */
export function toParagraphs(text: string): string[] {
  return text
    .replace(/([。！？])/g, "$1\n")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

/** `2026-07-31T13:27:00.000Z` → `13:27`。解析不了返回空串。 */
function hhmm(at: Date): string {
  return at.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** 两个时刻在**看的人所在时区**是不是同一天。 */
function sameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

/**
 * 一段对话的时间范围：`【13:27 ~16:29】`，跨零点时是 `【19:45 ~次日11:45】`。
 *
 * ⚠ **`~` 前面那个空格是照抄的，不是笔误。** 旧版
 * （`CharacterRelationDiaryModule.tsx:122-127`）把它写在模板字符串里跨了行，
 * 缩进变成了一个空格，于是所有用户看了两年的都是这个样子。这里保留它，
 * 让新旧界面在同一个位置长得一样。
 *
 * ## 「次日」是有意加的，旧版没有
 *
 * 旧系统的会话边界形同虚设（`chatId` 由客户端传，没有任何时间检查），
 * 库里真有跨零点甚至跨天的会话——实测某条是 19:45 聊到次日 11:45。
 * 照旧版原样渲染就是 `【19:45 ~11:45】`，读起来像时间倒流。
 * 加一个「次日」两个字，比让用户以为界面坏了强。
 *
 * 只区分「同一天 / 不是同一天」，不写「第 3 天」：新系统的会话边界由服务端
 * 每轮判定，跨两天以上不会再出现，那种数据只可能是迁移来的遗留。
 *
 * ## 时间按**看的人**所在时区渲染
 *
 * 与聊天页的 `formatTime` 同一套（`MessageBubble.tsx:51`），全站一致。
 * 而日界是服务端按业务时区（Asia/Shanghai）划的，所以人在别的时区时，
 * 页面标题写着 07-25 而里面某段显示 04:24，两者会对不上。
 * 这个产品当前只面向国内，不为此在前端再复制一份时区常量
 * （`@dreamverse/config` 的 `BUSINESS_TIME_ZONE`，web 目前不依赖 config）。
 * 真要改成固定时区渲染，聊天页那处要一起改，否则同一条消息两个地方两个时间。
 *
 * `finishedAt` 为 null（迁移来的旧数据）时只显示起点，不编一个结束时间。
 *
 * ## 结束早于开始时只显示起点
 *
 * 听起来不可能，2026-08-01 实测本地库 10,493 条会话摘要里 **10,416 条如此**。
 * 成因是服务端把 JS `Date` 当参数塞进裸 SQL：Prisma 送的是 `timestamptz`，
 * 而这些列是 `timestamp`（without time zone），赋值时按**会话时区**
 * （Asia/Shanghai）折算，于是整体偏 8 小时。已经修完了
 * （`shared/prisma/prisma.service.ts` 钉连接串 + `sql.ts` 的 `utcTs()` +
 * `tools/backfill-memory-timestamps.ts` 重算存量）。
 *
 * **这道判断仍然保留**：它挡的不是那一次的 bug，而是「服务端给了不可能的
 * 数据」这一类。`【11:45 ~03:45】` 会被读成时间倒流，用户只会觉得界面坏了。
 * 宁可只说「11:45 那会儿」。
 */
export function timeRangeLabel(startedAt: string, finishedAt: string | null): string {
  const from = new Date(startedAt);
  if (Number.isNaN(from.getTime())) return "";

  const to = finishedAt ? new Date(finishedAt) : null;
  if (!to || Number.isNaN(to.getTime()) || to < from) return `【${hhmm(from)}】`;

  return `【${hhmm(from)} ~${sameDay(from, to) ? "" : "次日"}${hhmm(to)}】`;
}
